import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { collisionDeSource, estMessageDelta } from '../../../lib/plan.js';
import {
  compteursCoherents,
  lireRapport,
  type LigneRapport,
  type RapportLot,
} from '../../../lib/reprise.js';
import { messageReprise } from './messages.js';
import { ReferenceOrigine } from './plan/ReferenceOrigine.js';
import { RevoirBloc } from './plan/RevoirBloc.js';
import planStyles from './plan/Plan.module.css';
import styles from './Reprise.module.css';

function Lignes({
  titre,
  lignes,
  ton,
}: {
  titre: string;
  lignes: readonly LigneRapport[];
  ton: 'erreur' | 'avertissement';
}): JSX.Element | null {
  const { t } = useTranslation('console');
  if (lignes.length === 0) return null;
  return (
    <section className={styles.lignes} aria-label={titre}>
      <h4>{titre}</h4>
      <ul className={styles.listeLignes} data-ton={ton} tabIndex={0}>
        {lignes.map((ligne, rang) => (
          <li key={rang}>
            <strong>
              {t(
                ligne.fichier === 'accrochages'
                  ? 'reprise.rapport.ligneAccrochage'
                  : 'reprise.rapport.ligne',
                {
                  ligne: ligne.ligne,
                  libelle: ligne.libelle,
                },
              )}
            </strong>
            <ul>
              {ligne.messages.map((message, i) => {
                const reference = collisionDeSource(message);
                return (
                  <li key={i}>
                    {message}
                    {reference !== null ? (
                      <span className={planStyles.collision}>
                        <strong>{t('plan.origine.collision.titre')}</strong>
                        <span>
                          <ReferenceOrigine valeur={reference} />
                        </span>
                        <span>{t('plan.origine.collision.texte')}</span>
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Le rapport d'un essai ou d'un import. C'est le seul endroit de la console où
 * une ligne du fichier peut apparaître (son numéro, son libellé, ce qui la
 * refuse) ; les compteurs sont ceux du rapport, sans recalcul.
 */
export function RapportContenu({
  rapport,
  type,
}: {
  rapport: RapportLot;
  type: 'essai' | 'final';
}): JSX.Element {
  const { t } = useTranslation('console');
  const details = Object.entries(rapport.details).filter(([, nombre]) => nombre > 0);
  const listes = Object.entries(rapport.listes).filter(([, valeurs]) => valeurs.length > 0);
  const libelleCompteur = (cle: string, defaut: string): string =>
    String(t(cle, { defaultValue: defaut }));
  // Les lignes du delta ont leur liste à revoir : leur avertissement n'est pas répété parmi les « à vérifier ».
  const avertissements =
    rapport.revoir.length === 0
      ? rapport.avertissements
      : rapport.avertissements
          .map((ligne) => ({
            ...ligne,
            messages: ligne.messages.filter((message) => !estMessageDelta(message)),
          }))
          .filter((ligne) => ligne.messages.length > 0);
  return (
    <div className={styles.rapport}>
      <h3>{t(type === 'essai' ? 'reprise.rapport.titreEssai' : 'reprise.rapport.titreFinal')}</h3>
      {type === 'essai' ? <p className={styles.discret}>{t('reprise.rapport.aideEssai')}</p> : null}
      <dl className={styles.compteurs} role="group" aria-label={t('reprise.rapport.compteurs')}>
        <div data-compteur="total">
          <dt>{t('reprise.rapport.total')}</dt>
          <dd>{rapport.total}</dd>
        </div>
        <div data-compteur="crees">
          <dt>{t('reprise.rapport.crees')}</dt>
          <dd>{rapport.crees}</dd>
        </div>
        <div data-compteur="completes">
          <dt>{t('reprise.rapport.completes')}</dt>
          <dd>{rapport.completes}</dd>
        </div>
        <div data-compteur="ignores">
          <dt>{t('reprise.rapport.ignores')}</dt>
          <dd>{rapport.ignores}</dd>
        </div>
        <div data-compteur="refuses" data-refus={rapport.refuses > 0}>
          <dt>{t('reprise.rapport.refuses')}</dt>
          <dd>{rapport.refuses}</dd>
        </div>
      </dl>
      {!compteursCoherents(rapport) ? (
        <p className={styles.erreur} role="alert">
          {t('reprise.rapport.incoherent')}
        </p>
      ) : null}
      {details.length > 0 || listes.length > 0 ? (
        <ul className={styles.sources}>
          {details.map(([cle, nombre]) => (
            <li key={cle}>
              <span>
                {cle === 'signales'
                  ? t('plan.delta.signalees')
                  : libelleCompteur(`reprise.rapport.details.${cle}`, cle)}
              </span>
              <span>{nombre}</span>
            </li>
          ))}
          {listes.map(([cle, valeurs]) => (
            <li key={cle}>
              <span>{libelleCompteur(`reprise.rapport.listes.${cle}`, cle)}</span>
              <span>{valeurs.join(', ')}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <RevoirBloc lignes={rapport.revoir} />
      {rapport.colonnesInconnues.length > 0 ? (
        <p className={styles.discret}>
          {t('reprise.rapport.colonnesInconnues', { liste: rapport.colonnesInconnues.join(', ') })}
        </p>
      ) : null}
      {type === 'essai' && rapport.erreurs.length === 0 ? (
        <p className={styles.discret}>{t('reprise.rapport.aucuneErreur')}</p>
      ) : null}
      <Lignes titre={t('reprise.rapport.erreurs')} lignes={rapport.erreurs} ton="erreur" />
      <Lignes
        titre={t('reprise.rapport.avertissements')}
        lignes={avertissements}
        ton="avertissement"
      />
    </div>
  );
}

/** Le rapport d'un lot, relu à chaque nouvelle révision du lot. */
export function RapportVue({
  idGalerie,
  lotId,
  revision,
  type,
}: {
  idGalerie: string;
  lotId: string;
  revision: number;
  type: 'essai' | 'final';
}): JSX.Element {
  const { t } = useTranslation('console');
  const rapport = useQuery({
    queryKey: ['console', 'galerie', idGalerie, 'reprise', 'rapport', lotId, type, revision],
    queryFn: () => lireRapport(idGalerie, lotId, type),
    staleTime: Infinity,
  });
  if (rapport.isLoading) return <p className={styles.discret}>{t('reprise.chargement')}</p>;
  if (rapport.isError || !rapport.data) {
    return (
      <p className={styles.erreur} role="alert">
        {messageReprise(t, rapport.error) ?? t('reprise.rapport.indisponible')}
      </p>
    );
  }
  return <RapportContenu rapport={rapport.data} type={type} />;
}
